import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useQuery, useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
  GetOrganizationAddressesDocument,
  GetOrganizationsForAddressListDocument,
  CreateOrganizationAddressDocument,
  type GetOrganizationAddressesQuery,
  type GetOrganizationsForAddressListQuery,
  type AddressType,
  type CreateOrganizationAddressMutation,
  type CreateOrganizationAddressMutationVariables,
} from '@/generated/root-types';
import { DataTable } from '@gastrosoft/shared/ui';
import { setOverrideTenantId } from '@gastrosoft/shared/utils';
import { useNotification } from '@/app/contexts/NotificationContext';
import { GRAPHQL_MAX_PAGE_SIZE } from '@/utils/constants';
import { translateAddressType } from '@/utils/addressLabels';

const ADDRESS_TYPES: AddressType[] = [
  'HEADQUARTERS',
  'BRANCH',
  'INVOICE',
  'DELIVERY',
  'OTHER',
  'CUSTOMER_CONTACT',
];

interface AddrRow extends Record<string, unknown> {
  id: string;
  type: string;
  city: string;
  orgName: string;
  isDefault: boolean;
  line1: string;
  description: string | null;
  status: string | null;
  lat: string | null;
  lng: string | null;
  updatedAt: string;
  /** For billing API tenant header when opening address detail */
  tenantIdForNav: string | undefined;
}

export default function OrganizationAddresses() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatDateTime = (iso: string) => new Date(iso).toLocaleString(locale);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const tenantId = searchParams.get('tenantId') ?? undefined;
  const organizationId = searchParams.get('organizationId') ?? undefined;
  const { showSuccess, showError } = useNotification();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [open, setOpen] = useState(false);
  const [addrType, setAddrType] = useState<AddressType>('INVOICE');
  const [isDefault, setIsDefault] = useState(false);
  const [line1, setLine1] = useState('');
  const [city, setCity] = useState('');
  const [zip, setZip] = useState('');
  const [country, setCountry] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [line2, setLine2] = useState('');
  const [organizationUnitId, setOrganizationUnitId] = useState('');

  useEffect(() => {
    if (tenantId) setOverrideTenantId(tenantId);
    else setOverrideTenantId(null);
    return () => setOverrideTenantId(null);
  }, [tenantId]);

  const {
    data: byOrgData,
    loading: byOrgLoading,
    refetch: refetchByOrg,
  } = useQuery<GetOrganizationAddressesQuery>(GetOrganizationAddressesDocument, {
    variables: { first: GRAPHQL_MAX_PAGE_SIZE, where: { organizationId: { eq: organizationId! } } },
    skip: !organizationId,
  });

  const {
    data: orgsForAddrData,
    loading: orgsLoading,
    refetch: refetchOrgsForAddr,
  } = useQuery<GetOrganizationsForAddressListQuery>(GetOrganizationsForAddressListDocument, {
    variables: { first: GRAPHQL_MAX_PAGE_SIZE, tenantId: tenantId ?? null },
    skip: !!organizationId,
  });

  const loading = organizationId ? byOrgLoading : orgsLoading;

  const refetch = async () => {
    if (organizationId) await refetchByOrg();
    else await refetchOrgsForAddr();
  };

  const nodes = useMemo(() => {
    if (organizationId) {
      return byOrgData?.organizationAddresses?.nodes ?? [];
    }
    const orgNodes = orgsForAddrData?.organizations?.nodes ?? [];
    return orgNodes.flatMap((o) => o.addresses ?? []);
  }, [organizationId, byOrgData, orgsForAddrData]);

  const [createAddr, { loading: creating }] = useMutation<
    CreateOrganizationAddressMutation,
    CreateOrganizationAddressMutationVariables
  >(CreateOrganizationAddressDocument);

  const rows: AddrRow[] = useMemo(
    () =>
      nodes.map((n) => ({
        id: n.id,
        type: n.type,
        city: n.city ?? '–',
        orgName: n.organization?.name ?? '–',
        isDefault: n.isDefault,
        line1: n.addressLine1 ?? '–',
        description: n.description ?? null,
        status: n.status ?? null,
        lat: n.latitude != null ? String(n.latitude) : null,
        lng: n.longitude != null ? String(n.longitude) : null,
        updatedAt: n.updatedAt,
        tenantIdForNav: n.tenant?.id,
      })),
    [nodes],
  );

  const columns: GridColDef[] = [
    {
      field: 'type',
      headerName: t('common.type'),
      width: 150,
      valueFormatter: (v: string) => translateAddressType(t, v),
    },
    { field: 'line1', headerName: t('orgAddresses.line1'), flex: 1, minWidth: 120 },
    { field: 'city', headerName: t('orgAddresses.city'), flex: 1, minWidth: 100 },
    { field: 'orgName', headerName: t('organizationDetail.customer'), flex: 1, minWidth: 130 },
    {
      field: 'isDefault',
      headerName: t('common.default'),
      width: 130,
      valueFormatter: (v: boolean) => (v ? t('orgAddresses.standardAddress') : '–'),
    },
    {
      field: 'description',
      headerName: t('orgAddresses.addressDescription'),
      width: 140,
      valueFormatter: (v: string | null) => (v && v.length > 40 ? `${v.slice(0, 40)}…` : v || '–'),
    },
    {
      field: 'status',
      headerName: t('orgAddresses.status'),
      width: 100,
      valueFormatter: (v: string | null) => v || '–',
    },
    {
      field: 'lat',
      headerName: t('orgAddresses.latitude'),
      width: 100,
      valueFormatter: (v: string | null) => v || '–',
    },
    {
      field: 'lng',
      headerName: t('orgAddresses.longitude'),
      width: 100,
      valueFormatter: (v: string | null) => v || '–',
    },
    {
      field: 'updatedAt',
      headerName: t('common.updated'),
      width: 150,
      valueFormatter: (v: string) => formatDateTime(v),
    },
  ];

  const handleCreate = async () => {
    if (!organizationId) {
      showError(t('orgAddresses.needOrganization'));
      return;
    }
    try {
      const res = await createAddr({
        variables: {
          input: {
            organizationId,
            type: addrType,
            isDefault,
            addressLine1: line1 || undefined,
            addressLine2: line2.trim() || undefined,
            city: city || undefined,
            zipCode: zip || undefined,
            country: country || undefined,
            phoneNumber: phone || undefined,
            email: email || undefined,
            organizationUnitId: organizationUnitId.trim() || undefined,
          },
        },
      });
      const top = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
      if (top?.length) {
        showError(top.map((e) => e.message).join(' '));
        return;
      }
      if (!res.data?.createOrganizationAddress?.organizationAddress) {
        showError(t('errors.generic'));
        return;
      }
      showSuccess(t('orgAddresses.created'));
      setOpen(false);
      setLine1('');
      setLine2('');
      setCity('');
      setZip('');
      setCountry('');
      setPhone('');
      setEmail('');
      setOrganizationUnitId('');
      await refetch();
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  return (
    <Box sx={{ height: 'calc(100vh - 180px)' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="h4">{t('orgAddresses.title')}</Typography>
        <Button variant="contained" onClick={() => setOpen(true)} disabled={!organizationId}>
          {t('orgAddresses.create')}
        </Button>
      </Box>

      <DataTable<AddrRow>
        enableMobileCards
        columns={columns}
        data={rows}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={rows.length}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        paginationMode="client"
        onRowClick={(row) =>
          navigate(`/organization-addresses/${row.id}`, {
            state: {
              addressesListSearch: location.search,
              tenantIdOverride: row.tenantIdForNav ?? tenantId,
            },
          })
        }
      />

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{t('orgAddresses.create')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            select
            label={t('common.type')}
            value={addrType}
            onChange={(e) => setAddrType(e.target.value as AddressType)}
            fullWidth
            sx={{ mt: 2 }}
          >
            {ADDRESS_TYPES.map((x) => (
              <MenuItem key={x} value={x}>
                {translateAddressType(t, x)}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Switch checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
            }
            label={t('orgAddresses.standardAddress')}
          />
          <TextField
            label={t('orgAddresses.line1')}
            value={line1}
            onChange={(e) => setLine1(e.target.value)}
            fullWidth
          />
          <TextField
            label={t('orgAddresses.line2')}
            value={line2}
            onChange={(e) => setLine2(e.target.value)}
            fullWidth
          />
          <TextField
            label={t('orgAddresses.organizationUnitId')}
            value={organizationUnitId}
            onChange={(e) => setOrganizationUnitId(e.target.value)}
            fullWidth
          />
          <TextField
            label={t('orgAddresses.city')}
            value={city}
            onChange={(e) => setCity(e.target.value)}
            fullWidth
          />
          <TextField
            label={t('orgAddresses.zip')}
            value={zip}
            onChange={(e) => setZip(e.target.value)}
            fullWidth
          />
          <TextField
            label={t('orgAddresses.country')}
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            fullWidth
          />
          <TextField
            label={t('organizationDetail.phone')}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            fullWidth
          />
          <TextField
            label={t('organizationDetail.email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating || !organizationId}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
